<template>
  <div
    ref="rootRef"
    :class="['pull-refresh', `pull-refresh--${look}`]">
    <div
      v-if="indicatorShown"
      class="pull-refresh__indicator"
      :class="{ 'is-settling': !dragging }"
      :style="indicatorStyle"
      aria-hidden="true">
      <q-spinner-ios
        v-if="look === 'ios'"
        size="26px"
        class="pull-refresh__ios-spinner"
        :style="{ opacity: refreshing ? 1 : progress }" />
      <div
        v-else
        class="pull-refresh__disc">
        <q-spinner
          v-if="refreshing"
          size="22px"
          color="primary"
          :thickness="5" />
        <q-icon
          v-else
          :name="symRoundedRefresh"
          size="24px"
          color="primary"
          :style="{ opacity: 0.4 + 0.6 * progress, transform: `rotate(${progress * 270}deg)` }" />
      </div>
    </div>
    <div
      class="pull-refresh__content"
      :class="{ 'is-settling': !dragging }"
      :style="contentStyle">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Pull down from the top of a screen to refresh its data, drawn the way each
 * platform draws it: on iOS the screen follows the finger and a spinner sits
 * in the gap above it; on Android a Material disc slides down over the screen,
 * which stays put. Screens opt in with `usePullToRefresh`.
 */
import { symRoundedRefresh } from '@quasar/extras/material-symbols-rounded'
import { useQuasar } from 'quasar'
import { computed, onBeforeUnmount, onMounted, provide, ref } from 'vue'
import type { RefreshTarget } from './pullToRefresh'
import { isScrolledToTop, ownsVerticalDrag, pickTarget, refreshRegistryKey, rubberBand } from './pullToRefresh'

/** The pull never travels past this, however far the finger goes. */
const MAX_PULL = 140
/** Releasing past this refreshes; about 120px of finger travel. */
const THRESHOLD = 64
/** Where the indicator rests while the refresh runs. */
const HOLD = { ios: 52, material: 64 }
/** Below this, the finger is still settling on a direction. */
const DIRECTION_SLOP = 3
/** A fast refresh still shows its spinner long enough to read as one. */
const MIN_SPIN_MS = 400
/** Matches the settle transition in the styles below. */
const SETTLE_MS = 250

const $q = useQuasar()
const look = $q.platform.is.ios ? 'ios' : 'material'

const rootRef = ref<HTMLElement | null>(null)
const distance = ref(0)
const dragging = ref(false)
const refreshing = ref(false)
const settling = ref(false)

const targets: RefreshTarget[] = []
provide(refreshRegistryKey, {
  register(target) {
    targets.push(target)
    return () => {
      const index = targets.indexOf(target)
      if (index !== -1) targets.splice(index, 1)
    }
  },
})

const progress = computed(() => Math.min(1, distance.value / THRESHOLD))
const indicatorShown = computed(() => distance.value > 0 || refreshing.value || settling.value)

const indicatorStyle = computed(() =>
  look === 'ios' ? { height: `${distance.value}px` } : { transform: `translate(-50%, ${distance.value - 48}px)` },
)

// No transform at rest: a transformed ancestor would pin the screen's fixed
// elements, such as the floating bulk-action bar, to it instead of the viewport.
const contentStyle = computed(() =>
  look === 'ios' && indicatorShown.value ? { transform: `translateY(${distance.value}px)` } : undefined,
)

interface Gesture {
  x: number
  y: number
  target: RefreshTarget
  engaged: boolean
}

let gesture: Gesture | null = null
let settleTimer: ReturnType<typeof setTimeout> | undefined
let unmounted = false

function settle() {
  dragging.value = false
  distance.value = 0
  settling.value = true
  clearTimeout(settleTimer)
  settleTimer = setTimeout(() => (settling.value = false), SETTLE_MS)
}

async function runRefresh(target: RefreshTarget) {
  dragging.value = false
  refreshing.value = true
  distance.value = HOLD[look]
  const started = Date.now()
  try {
    await target.refresh()
  } catch (error) {
    // the screen reports its own failures; the spinner only has to stop
    console.error(error)
  }
  const remaining = MIN_SPIN_MS - (Date.now() - started)
  if (remaining > 0) await new Promise((resolve) => setTimeout(resolve, remaining))
  if (unmounted) return
  refreshing.value = false
  settle()
}

function onTouchStart(event: TouchEvent) {
  // a second finger landing mid-pull calls the pull off
  if (gesture?.engaged) {
    onTouchCancel()
    return
  }
  gesture = null
  if (refreshing.value) return
  const node = event.target
  if (!(node instanceof Element) || ownsVerticalDrag(node) || !isScrolledToTop(node)) return
  const target = pickTarget(targets, node)
  if (!target) return
  const touch = event.touches[0]!
  gesture = { x: touch.clientX, y: touch.clientY, target, engaged: false }
}

function onTouchMove(event: TouchEvent) {
  if (!gesture) return
  if (event.touches.length !== 1) {
    onTouchCancel()
    return
  }
  const touch = event.touches[0]!
  const dx = touch.clientX - gesture.x
  const dy = touch.clientY - gesture.y

  if (!gesture.engaged) {
    if (Math.abs(dx) < DIRECTION_SLOP && Math.abs(dy) < DIRECTION_SLOP) return
    // up, or mostly sideways (dy <= 0 always fails this): a scroll or a swipe
    if (Math.abs(dx) > dy) {
      gesture = null
      return
    }
    gesture.engaged = true
    dragging.value = true
    settling.value = false
    clearTimeout(settleTimer)
  }

  // the pull owns the gesture now: no scroll, and no iOS bounce under it
  if (event.cancelable) event.preventDefault()
  distance.value = rubberBand(dy, MAX_PULL)
}

function onTouchEnd() {
  const ended = gesture
  gesture = null
  if (!ended?.engaged) return
  if (distance.value >= THRESHOLD) void runRefresh(ended.target)
  else settle()
}

function onTouchCancel() {
  const ended = gesture
  gesture = null
  if (ended?.engaged) settle()
}

onMounted(() => {
  const root = rootRef.value
  if (!root) return
  root.addEventListener('touchstart', onTouchStart, { passive: true })
  // not passive: an engaged pull has to cancel the scroll it would start
  root.addEventListener('touchmove', onTouchMove, { passive: false })
  root.addEventListener('touchend', onTouchEnd, { passive: true })
  root.addEventListener('touchcancel', onTouchCancel, { passive: true })
})

onBeforeUnmount(() => {
  unmounted = true
  clearTimeout(settleTimer)
  const root = rootRef.value
  if (!root) return
  root.removeEventListener('touchstart', onTouchStart)
  root.removeEventListener('touchmove', onTouchMove)
  root.removeEventListener('touchend', onTouchEnd)
  root.removeEventListener('touchcancel', onTouchCancel)
})
</script>

<style lang="scss" scoped>
.pull-refresh {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
}

.pull-refresh__content {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
}

.pull-refresh__indicator {
  position: absolute;
  top: 0;
  pointer-events: none;
}

.pull-refresh--ios .pull-refresh__indicator {
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.pull-refresh__ios-spinner {
  color: #8e8e93;
}

.pull-refresh--material .pull-refresh__indicator {
  left: 50%;
  // under the header (2000), over the screen's own sticky bars
  z-index: 1000;
}

.pull-refresh__disc {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #fff;
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.2),
    0 2px 6px rgba(0, 0, 0, 0.12);

  .body--dark & {
    background: #2c2c2e;
  }
}

.pull-refresh__content.is-settling,
.pull-refresh__indicator.is-settling {
  transition:
    transform 250ms cubic-bezier(0.2, 0.9, 0.3, 1),
    height 250ms cubic-bezier(0.2, 0.9, 0.3, 1);
}
</style>
